import type { RouteRecordRaw } from "vue-router";

import adminRoutes from "./admin/index";
import authRoutes from "./auth/index";
import productRoutes from "./core/index";
import { RouteNames } from "./types/names";
import type { AppRouteRecordRaw } from "./types/types";
import { filterRoutesByPermissions, routesToMenuItems } from "./utils/utils";

import { useAuthStore } from "@/features/auth/store/authStore";
import type { Partnership } from "@/features/partnership/types";

/**
 * All application routes
 */
const modules: Array<AppRouteRecordRaw> = [
  // Root route - handled by authGuard which redirects to first accessible route
  // Component is required because redirect happens BEFORE guards
  {
    path: "/",
    name: "Root",
    meta: {
      title: "Home",
      showInMenu: false,
      isRootRedirect: true, // Special flag to trigger smart redirect in guard
      partnerships: ["seller", "vendor"],
    },
  },

  // 403 Forbidden page (public)
  {
    path: "/403",
    name: "Forbidden",
    component: () => import("@/pages/ForbiddenAccess.vue"),
    meta: {
      title: "Forbidden",
      showInMenu: false,
      partnerships: ["seller", "vendor"],
      filters: {
        show: [],
      },
    },
  },
  // Feature routes
  ...productRoutes,
  ...adminRoutes,
  ...authRoutes,
  {
    path: "/:pathMatch(.*)*",
    name: RouteNames.NOT_FOUND,
    component: () => import("@/pages/NotFound.vue"),
    meta: {
      title: "Not Found",
      showInMenu: false,
      partnerships: ["seller", "vendor"],
      filters: {
        show: [],
      },
    },
  },
];

/**
 * Routes that should appear in main menu
 * Automatically filtered from modules based on meta.showInMenu
 */
export const menuModules = modules.filter(
  route => route.meta?.showInMenu !== false && route.name,
);

/**
 * Get menu items filtered by current user permissions and active partnership
 * Call this function with authStore to get fresh menu items based on user's current permissions
 * Use in computed properties for reactivity
 * @param authStore - Auth store instance
 * @param partnership - Active partnership
 * @returns Filtered menu items based on user permissions and partnership
 */
export function getMenuItems(
  authStore: ReturnType<typeof useAuthStore>,
  partnership: Partnership,
) {
  return routesToMenuItems(
    filterRoutesByPermissions(menuModules as RouteRecordRaw[], authStore, partnership),
  );
}

export default modules as Array<RouteRecordRaw>;
